import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { Zap, TrendingDown, CheckCircle, X, ArrowRight, Sparkles, ShoppingBag, DollarSign } from 'lucide-react';
export function ComparisonArticlePage() {
  useEffect(() => {
    // SEO meta tags
    document.title = 'ShoppingExpert.ai vs ChatGPT: Which One Is Better for Product Decisions? | 2025 Comparison';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', "ChatGPT is powerful, but is it the best tool for shopping and product decisions? Learn how ShoppingExpert.ai's multi-agent system compares in 2025.");
    }
    // Scroll to top
    window.scrollTo(0, 0);
  }, []);
  return <div className="min-h-screen bg-slate-50" data-id="element-573">
      {/* Header */}
      <header className="sticky top-0 z-10 backdrop-blur supports-[backdrop-filter]:bg-white/70 bg-white/90 border-b border-slate-200" data-id="element-574">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-4" data-id="element-575">
          <div className="flex items-center justify-between" data-id="element-576">
            <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity" data-id="element-577">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/4yJ3FscyPfrN1xAm7crLNW/ChatGPT_Image_Dec_2%2C_2025%2C_10_42_07_PM.png" alt="Shopping Expert.ai Logo" className="h-12 object-contain" data-id="element-578" />
              <span className="text-xl font-bold text-slate-900" data-id="element-579">
                AI Shopping Expert
              </span>
            </Link>
            <nav className="hidden md:flex items-center gap-6 text-sm" data-id="element-580">
              <Link to="/" className="text-slate-600 hover:text-slate-900 font-medium" data-id="element-581">
                Home
              </Link>
              <Link to="/research" className="text-slate-600 hover:text-slate-900 font-medium" data-id="element-582">
                Research Tools
              </Link>
              <Link to="/about" className="text-slate-600 hover:text-slate-900 font-medium" data-id="element-583">
                About
              </Link>
              <Link to="/contact" className="text-slate-600 hover:text-slate-900 font-medium" data-id="element-584">
                Contact
              </Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="mx-auto max-w-4xl px-4 sm:px-6 py-12" data-id="element-585">
        <Breadcrumbs items={[{
        label: 'Blog',
        href: '/blog'
      }, {
        label: 'ShoppingExpert.ai vs ChatGPT',
        href: '/shoppingexpert-vs-chatgpt'
      }]} data-id="element-586" />

        {/* Article Header */}
        <article className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 sm:p-12" data-id="element-587">
          <div className="mb-8" data-id="element-588">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-sm font-semibold mb-4" data-id="element-589">
              <Sparkles size={16} data-id="element-590" />
              Comparison Guide
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4 leading-tight" data-id="element-591">
              ShoppingExpert.ai vs ChatGPT: Which One Is Better for Product
              Decisions?
            </h1>
            <p className="text-lg text-slate-600" data-id="element-592">
              ChatGPT is great for general questions — but is it the best tool
              for product research and buying decisions? This guide compares
              ChatGPT to ShoppingExpert.ai, a multi-agent system built for smart
              shopping in 2025.
            </p>
          </div>

          {/* Introduction */}
          <div className="prose prose-slate max-w-none mb-12" data-id="element-593">
            <h2 className="text-3xl font-bold text-slate-900 mb-4" data-id="element-594">
              Introduction
            </h2>
            <p className="text-lg text-slate-700 leading-relaxed mb-4" data-id="element-595">
              ChatGPT changed everything. It became the world's most popular AI
              tool for learning, summarizing, and solving everyday problems.
            </p>
            <p className="text-lg text-slate-700 leading-relaxed mb-4" data-id="element-596">
              But when it comes to product research, price comparison, and
              shopping decisions, the question is different:
            </p>
            <p className="text-xl text-slate-900 leading-relaxed mb-4 font-semibold" data-id="element-597">
              Should you use ChatGPT — or a specialized AI like
              ShoppingExpert.ai?
            </p>
            <p className="text-lg text-slate-700 leading-relaxed" data-id="element-598">
              In this guide, we compare both tools and explain why
              ShoppingExpert.ai was built specifically to solve the problems
              ChatGPT can't. Learn more about{' '}
              <Link to="/what-is-ai-shopping-assistant" className="text-purple-600 hover:text-purple-700 underline" data-id="element-599">
                what AI shopping assistants are
              </Link>{' '}
              and how they work.
            </p>
          </div>

          {/* ChatGPT Section */}
          <div className="mb-12" data-id="element-600">
            <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3" data-id="element-601">
              🤖 ChatGPT: Amazing General AI — Limited for Shopping
            </h2>
            <p className="text-lg text-slate-700 leading-relaxed mb-6" data-id="element-602">
              ChatGPT is powerful, but it's designed for general intelligence,
              not specialized shopping tasks.
            </p>

            <div className="grid md:grid-cols-2 gap-6 mb-6" data-id="element-603">
              {/* What ChatGPT Does Well */}
              <div className="p-6 rounded-xl bg-green-50 border-2 border-green-200" data-id="element-604">
                <h3 className="text-xl font-bold text-green-900 mb-4 flex items-center gap-2" data-id="element-605">
                  <CheckCircle size={24} className="text-green-600" data-id="element-606" />
                  What ChatGPT does well:
                </h3>
                <ul className="space-y-2" data-id="element-607">
                  {['Summaries', 'General explanations', 'Brainstorming', 'Conversations', 'Simple comparisons', 'Information you already know'].map((item, idx) => <li key={idx} className="flex items-center gap-2 text-slate-700" data-id="element-608">
                      <CheckCircle size={16} className="text-green-600 flex-shrink-0" data-id="element-609" />
                      <span data-id="element-610">{item}</span>
                    </li>)}
                </ul>
              </div>

              {/* What ChatGPT Doesn't Do Well */}
              <div className="p-6 rounded-xl bg-red-50 border-2 border-red-200" data-id="element-611">
                <h3 className="text-xl font-bold text-red-900 mb-4 flex items-center gap-2" data-id="element-612">
                  <X size={24} className="text-red-600" data-id="element-613" />
                  What ChatGPT does NOT do well for shopping:
                </h3>
                <ul className="space-y-2" data-id="element-614">
                  {['Real-time price comparison', 'Cross-store analysis', 'Coupon detection', 'Deep product review mining', 'Identifying hidden issues', 'Import tax calculations', 'Final price breakdown', 'Multi-model elimination', 'Seasonal shopping strategy'].map((item, idx) => <li key={idx} className="flex items-center gap-2 text-slate-700" data-id="element-615">
                      <X size={16} className="text-red-600 flex-shrink-0" data-id="element-616" />
                      <span data-id="element-617">{item}</span>
                    </li>)}
                </ul>
              </div>
            </div>

            <p className="text-lg text-slate-700 leading-relaxed font-semibold" data-id="element-618">
              ChatGPT is like a very smart friend — but it's not trained to be
              your personal shopping researcher.
            </p>
          </div>

          {/* ShoppingExpert.ai Section */}
          <div className="mb-12 p-8 rounded-2xl bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-200" data-id="element-619">
            <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3" data-id="element-620">
              <Zap className="text-purple-600" size={32} data-id="element-621" />
              🧠 ShoppingExpert.ai: Purpose-Built for Smart Buying Decisions
            </h2>
            <p className="text-lg text-slate-700 leading-relaxed mb-6" data-id="element-622">
              ShoppingExpert.ai is different because it's{' '}
              <strong data-id="element-623">specialized</strong>.
            </p>
            <p className="text-lg text-slate-700 leading-relaxed mb-6" data-id="element-624">
              It uses a network of dedicated AI agents, each trained for a
              specific shopping domain:
            </p>

            <div className="space-y-3" data-id="element-625">
              {[{
              icon: '💻',
              title: 'Electronics AI Expert',
              desc: 'Finds the best TVs, laptops, headphones, monitors.',
              link: '/?category=general'
            }, {
              icon: '👔',
              title: 'Fashion AI Stylist',
              desc: 'Helps with fit, style, materials, and product alternatives.',
              link: '/?category=fashion'
            }, {
              icon: '🏠',
              title: 'Home & Kitchen AI Advisor',
              desc: 'Evaluates build quality, durability, and user complaints.',
              link: '/?category=home'
            }, {
              icon: '🎟️',
              title: 'AI Coupon Finder',
              desc: 'Discovers hidden codes and applies stacking strategies.',
              link: '/?category=shopping'
            }, {
              icon: '💰',
              title: 'AI Price Comparison Agent',
              desc: 'Checks Amazon, BestBuy, Walmart, Target, and more.',
              link: '/?category=shopping'
            }, {
              icon: '🌍',
              title: 'AI Import & Tax Calculator',
              desc: 'Tells you the real final price before you buy.',
              link: '/?category=shopping'
            }, {
              icon: '📊',
              title: 'AI Deal Tracker',
              desc: 'Alerts you when prices drop or when "not to buy."',
              link: '/?category=shopping'
            }].map((agent, idx) => <div key={idx} className="p-4 rounded-xl bg-white border border-purple-200 hover:border-purple-300 transition-colors" data-id="element-626">
                  <div className="flex items-start gap-3" data-id="element-627">
                    <span className="text-2xl flex-shrink-0" data-id="element-628">{agent.icon}</span>
                    <div data-id="element-629">
                      <h3 className="font-bold text-lg text-purple-900 mb-1" data-id="element-630">
                        {agent.title}
                      </h3>
                      <p className="text-slate-700 text-sm" data-id="element-631">{agent.desc}</p>
                    </div>
                  </div>
                </div>)}
            </div>

            <p className="text-lg text-slate-900 leading-relaxed mt-6 font-bold text-center" data-id="element-632">
              ChatGPT cannot do these.
            </p>
          </div>

          {/* Comparison Table */}
          <div className="mb-12" data-id="element-633">
            <h2 className="text-3xl font-bold text-slate-900 mb-6" data-id="element-634">
              🔎 Side-by-Side Comparison Table
            </h2>
            <div className="overflow-x-auto" data-id="element-635">
              <table className="w-full border-collapse" data-id="element-636">
                <thead data-id="element-637">
                  <tr className="bg-slate-100" data-id="element-638">
                    <th className="border border-slate-300 p-4 text-left font-bold" data-id="element-639">
                      Feature
                    </th>
                    <th className="border border-slate-300 p-4 text-center font-bold" data-id="element-640">
                      ChatGPT
                    </th>
                    <th className="border border-slate-300 p-4 text-center font-bold bg-purple-50" data-id="element-641">
                      ShoppingExpert.ai
                    </th>
                  </tr>
                </thead>
                <tbody data-id="element-642">
                  {[{
                  feature: 'Real-time price comparison',
                  chatgpt: false,
                  shopping: true
                }, {
                  feature: 'Multi-agent system',
                  chatgpt: false,
                  shopping: true
                }, {
                  feature: 'Review mining & problem detection',
                  chatgpt: false,
                  shopping: true
                }, {
                  feature: 'Coupon discovery',
                  chatgpt: false,
                  shopping: true
                }, {
                  feature: 'Best alternative finder',
                  chatgpt: 'partial',
                  shopping: true
                }, {
                  feature: 'Import tax calculation',
                  chatgpt: false,
                  shopping: true
                }, {
                  feature: 'Personalized pre-research workflow',
                  chatgpt: false,
                  shopping: true
                }, {
                  feature: 'Dedicated electronics expert',
                  chatgpt: false,
                  shopping: true
                }, {
                  feature: 'Works on WhatsApp (soon)',
                  chatgpt: false,
                  shopping: true
                }, {
                  feature: 'U.S. shopping specialization',
                  chatgpt: false,
                  shopping: true
                }].map((row, idx) => <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'} data-id="element-643">
                      <td className="border border-slate-300 p-4 font-medium" data-id="element-644">
                        {row.feature}
                      </td>
                      <td className="border border-slate-300 p-4 text-center" data-id="element-645">
                        {row.chatgpt === true ? <CheckCircle size={24} className="text-green-600 mx-auto" data-id="element-646" /> : row.chatgpt === 'partial' ? <span className="text-yellow-600 font-semibold" data-id="element-647">
                            ⚠️ Partial
                          </span> : <X size={24} className="text-red-600 mx-auto" data-id="element-648" />}
                      </td>
                      <td className="border border-slate-300 p-4 text-center bg-purple-50" data-id="element-649">
                        {row.shopping ? <CheckCircle size={24} className="text-green-600 mx-auto" data-id="element-650" /> : <X size={24} className="text-red-600 mx-auto" data-id="element-651" />}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pre-Research Workflow */}
          <div className="mb-12" data-id="element-652">
            <h2 className="text-3xl font-bold text-slate-900 mb-6" data-id="element-653">
              🔬 The Real Difference: Pre-Research Workflow
            </h2>
            <p className="text-lg text-slate-700 leading-relaxed mb-6" data-id="element-654">
              ShoppingExpert.ai runs a full research pipeline, not just a text
              answer:
            </p>

            <div className="space-y-4" data-id="element-655">
              {[{
              step: '1',
              title: 'Understand needs',
              desc: 'Usage, lifestyle, budget, preferences.'
            }, {
              step: '2',
              title: 'Shortlist products',
              desc: 'AI eliminates overpriced or low-quality models.'
            }, {
              step: '3',
              title: 'Compare real problems',
              desc: 'Warranty issues, common failures, battery decay, noise levels, defects.'
            }, {
              step: '4',
              title: 'Analyze price history',
              desc: 'Avoid bad times to buy.'
            }, {
              step: '5',
              title: 'Check coupons',
              desc: 'Stack promo codes + cashback.'
            }, {
              step: '6',
              title: 'Calculate final price',
              desc: 'Including shipping & import tax.'
            }, {
              step: '7',
              title: 'Give a final recommendation',
              desc: 'Clear, simple, personalized.'
            }].map((item, idx) => <div key={idx} className="flex gap-4 p-4 rounded-xl bg-slate-50 border border-slate-200" data-id="element-656">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-purple-600 text-white font-bold flex items-center justify-center" data-id="element-657">
                    {item.step}
                  </div>
                  <div data-id="element-658">
                    <h3 className="font-bold text-lg text-slate-900 mb-1" data-id="element-659">
                      {item.title}
                    </h3>
                    <p className="text-slate-700" data-id="element-660">{item.desc}</p>
                  </div>
                </div>)}
            </div>

            <div className="mt-6 p-6 rounded-xl bg-blue-50 border-2 border-blue-200" data-id="element-661">
              <p className="text-lg font-bold text-blue-900 text-center" data-id="element-662">
                ChatGPT doesn't do workflow-based research. ShoppingExpert.ai
                does this automatically.
              </p>
            </div>
          </div>

          {/* Money Savings */}
          <div className="mb-12" data-id="element-663">
            <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3" data-id="element-664">
              <DollarSign className="text-green-600" size={32} data-id="element-665" />
              💰 Which One Saves More Money?
            </h2>

            <div className="grid md:grid-cols-2 gap-6" data-id="element-666">
              <div className="p-6 rounded-xl bg-slate-50 border border-slate-200" data-id="element-667">
                <h3 className="text-xl font-bold text-slate-900 mb-3" data-id="element-668">
                  ChatGPT
                </h3>
                <p className="text-slate-700" data-id="element-669">
                  Gives general advice — no real savings.
                </p>
              </div>

              <div className="p-6 rounded-xl bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-300" data-id="element-670">
                <h3 className="text-xl font-bold text-green-900 mb-3" data-id="element-671">
                  ShoppingExpert.ai
                </h3>
                <p className="text-green-900 font-semibold mb-3" data-id="element-672">
                  Saves 10%–40% through:
                </p>
                <ul className="space-y-2" data-id="element-673">
                  {['Coupon discovery', 'Deal timing', 'Price comparison', 'Avoiding "trap" models', 'Recommending better-value alternatives', 'Finding trustworthy sellers'].map((item, idx) => <li key={idx} className="flex items-center gap-2 text-slate-700" data-id="element-674">
                      <CheckCircle size={16} className="text-green-600 flex-shrink-0" data-id="element-675" />
                      <span data-id="element-676">{item}</span>
                    </li>)}
                </ul>
              </div>
            </div>

            <p className="text-lg text-slate-700 leading-relaxed mt-6 font-semibold text-center" data-id="element-677">
              For real purchases, this is game-changing.
            </p>
          </div>

          {/* Multi-Channel Support */}
          <div className="mb-12" data-id="element-678">
            <h2 className="text-3xl font-bold text-slate-900 mb-6" data-id="element-679">
              🛠 Multi-Channel Support
            </h2>
            <p className="text-lg text-slate-700 leading-relaxed mb-4" data-id="element-680">
              ShoppingExpert.ai works across:
            </p>
            <ul className="space-y-3 mb-6" data-id="element-681">
              {['Website', 'WhatsApp (coming soon)', 'ChatGPT apps', 'Future voice/phone assistant'].map((item, idx) => <li key={idx} className="flex items-center gap-3 text-lg text-slate-700" data-id="element-682">
                  <CheckCircle size={20} className="text-purple-600 flex-shrink-0" data-id="element-683" />
                  <span data-id="element-684">{item}</span>
                </li>)}
            </ul>
            <p className="text-slate-600 italic" data-id="element-685">
              ChatGPT is limited to its own interface.
            </p>
          </div>

          {/* Conclusion */}
          <div className="mb-12 p-8 rounded-2xl bg-gradient-to-br from-slate-900 to-slate-800 text-white" data-id="element-686">
            <h2 className="text-3xl font-bold mb-6" data-id="element-687">
              🎯 Conclusion: Use ChatGPT for learning — use ShoppingExpert.ai
              for buying
            </h2>
            <div className="grid md:grid-cols-2 gap-8 mb-6" data-id="element-688">
              <div data-id="element-689">
                <h3 className="text-xl font-bold mb-4 text-slate-300" data-id="element-690">
                  Use ChatGPT for:
                </h3>
                <ul className="space-y-2" data-id="element-691">
                  {['Ideas', 'Summaries', 'Opinions', 'Explanations'].map((item, idx) => <li key={idx} className="flex items-center gap-2" data-id="element-692">
                        <span className="text-slate-400" data-id="element-693">•</span>
                        <span data-id="element-694">{item}</span>
                      </li>)}
                </ul>
              </div>
              <div data-id="element-695">
                <h3 className="text-xl font-bold mb-4 text-purple-300" data-id="element-696">
                  Use ShoppingExpert.ai for:
                </h3>
                <ul className="space-y-2" data-id="element-697">
                  {['Buying electronics', 'Finding coupons', 'Comparing stores', 'Discovering real product problems', 'Planning your shopping strategy', 'Getting the best deal today'].map((item, idx) => <li key={idx} className="flex items-center gap-2" data-id="element-698">
                      <CheckCircle size={16} className="text-green-400 flex-shrink-0" data-id="element-699" />
                      <span data-id="element-700">{item}</span>
                    </li>)}
                </ul>
              </div>
            </div>
            <p className="text-2xl font-bold text-center" data-id="element-701">
              ChatGPT = knowledge • ShoppingExpert.ai = decisions
            </p>
          </div>

          {/* Try It Yourself CTA */}
          <div className="mb-12 p-8 rounded-2xl bg-gradient-to-br from-purple-600 to-pink-600 text-white" data-id="element-702">
            <h2 className="text-3xl font-bold mb-6 flex items-center gap-3" data-id="element-703">
              <ShoppingBag size={32} data-id="element-704" />
              🛒 Try These Agents Now
            </h2>
            <p className="text-lg mb-6 opacity-90" data-id="element-705">
              Experience the difference:
            </p>
            <div className="space-y-3" data-id="element-706">
              <Link to="/?category=general" className="block w-full sm:w-auto px-6 py-3 bg-white text-purple-600 rounded-xl font-semibold hover:bg-slate-50 transition-colors text-center" data-id="element-707">
                👉 Talk to the Electronics AI Expert
              </Link>
              <Link to="/?category=shopping" className="block w-full sm:w-auto px-6 py-3 bg-white text-purple-600 rounded-xl font-semibold hover:bg-slate-50 transition-colors text-center" data-id="element-708">
                👉 Find hidden coupons with the AI Coupon Advisor
              </Link>
              <Link to="/?category=shopping" className="block w-full sm:w-auto px-6 py-3 bg-white text-purple-600 rounded-xl font-semibold hover:bg-slate-50 transition-colors text-center" data-id="element-709">
                👉 Compare Amazon vs BestBuy with the Price Comparison Agent
              </Link>
            </div>
          </div>

          {/* FAQ */}
          <div className="mb-12" data-id="element-710">
            <h2 className="text-3xl font-bold text-slate-900 mb-6" data-id="element-711">
              ❓ Frequently Asked Questions
            </h2>
            <div className="space-y-6" data-id="element-712">
              {[{
              q: 'Can ChatGPT give shopping advice?',
              a: 'Yes — but it lacks real-time data, price checks, and coupon analysis.'
            }, {
              q: 'Why is ShoppingExpert.ai more accurate?',
              a: 'Because it uses dedicated multi-agents trained specifically for the shopping domain.'
            }, {
              q: 'Does ShoppingExpert.ai work with Amazon?',
              a: 'Yes — it compares Amazon with other major U.S. retailers.'
            }, {
              q: 'Can I use it for fashion?',
              a: 'Yes — the fashion AI stylist analyzes fit, fabrics, style, and alternatives.'
            }, {
              q: 'Is it free?',
              a: 'Many features are free, with more advanced agents coming soon.'
            }].map((faq, idx) => <div key={idx} className="p-6 rounded-xl bg-slate-50 border border-slate-200" data-id="element-713">
                  <h3 className="font-bold text-lg text-slate-900 mb-2" data-id="element-714">
                    {faq.q}
                  </h3>
                  <p className="text-slate-700" data-id="element-715">{faq.a}</p>
                </div>)}
            </div>
          </div>

          {/* Final CTA */}
          <div className="text-center p-8 rounded-2xl bg-gradient-to-br from-slate-900 to-slate-800 text-white" data-id="element-716">
            <h2 className="text-2xl font-bold mb-4" data-id="element-717">
              📢 Ready to Make Smarter Buying Decisions?
            </h2>
            <p className="text-lg mb-6 opacity-90" data-id="element-718">
              Try the Electronics AI Expert and see why a specialized shopping
              assistant beats generic AI every time.
            </p>
            <Link to="/?category=general" className="inline-flex items-center gap-2 px-8 py-4 bg-white text-slate-900 rounded-xl font-bold text-lg hover:bg-slate-100 transition-colors" data-id="element-719">
              Get Started Free
              <ArrowRight size={20} data-id="element-720" />
            </Link>
          </div>
        </article>

        {/* Related Articles */}
        <div className="mt-12" data-id="element-721">
          <h3 className="text-2xl font-bold text-slate-900 mb-6" data-id="element-722">
            Related Articles
          </h3>
          <div className="grid sm:grid-cols-2 gap-6" data-id="element-723">
            <Link to="/what-is-ai-shopping-assistant" className="p-6 rounded-xl bg-white border border-slate-200 hover:border-slate-300 hover:shadow-md transition-all" data-id="element-724">
              <h4 className="font-bold text-lg text-slate-900 mb-2" data-id="element-725">
                What Is an AI Shopping Assistant?
              </h4>
              <p className="text-slate-600 text-sm" data-id="element-726">
                Complete guide to AI shopping assistants in 2025
              </p>
            </Link>
            <Link to="/" className="p-6 rounded-xl bg-white border border-slate-200 hover:border-slate-300 hover:shadow-md transition-all" data-id="element-727">
              <h4 className="font-bold text-lg text-slate-900 mb-2" data-id="element-728">
                Explore All AI Shopping Tools
              </h4>
              <p className="text-slate-600 text-sm" data-id="element-729">
                Browse 100+ AI assistants across 20 categories
              </p>
            </Link>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-white mt-16" data-id="element-730">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-8" data-id="element-731">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-500" data-id="element-732">
            <div data-id="element-733">© 2025 ShoppingExpert.ai • All rights reserved</div>
            <div className="flex items-center gap-3" data-id="element-734">
              <Link to="/about" className="underline" data-id="element-735">
                About
              </Link>
              <Link to="/contact" className="underline" data-id="element-736">
                Contact
              </Link>
              <Link to="/" className="underline" data-id="element-737">
                Home
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>;
}